import { FormattedMessage } from 'react-intl';
import * as baseStyles from '../../../styles/baseStyles.module.scss';
import * as buttonStyles from '../buttons/styles/styles.module.scss';
import * as styles from './styles/styles.module.scss';
import { validatePaymentForm } from '../../../utils/form_validaters/payment_form_helper';
import { hasErrors } from '../../../utils/form_validaters/user_form_helper';
import { LanguageSupportUtil } from '../../../utils/language_support_util';
import { ProPageStrings, StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { ServerCommunicationUtil } from '../../../utils/server_communication_util';
import CouponCode from '../CouponCode';
import CreditCardForm from '../CreditCardForm/CreditCardForm';
import CustomIntlProvider from 'components/CustomIntlProvider';
import FormButton from '../FormButton';
import PaymentFinePrint from 'components/PaymentFinePrint';
import PlanTypeCardCollection from '../pro/PlanTypeCardCollection';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Wrapper from '../Wrapper';

class UpdatePaymentForm extends Component {
  constructor(props) {
    super(props);
    const { billing_first_name, billing_last_name, cc_expiration_month, cc_expiration_year } = props.formData;

    this.state = {
      errors: {
        name: '',
        number: '',
        cvv: '',
        postalCode: ''
      },
      name: `${billing_first_name} ${billing_last_name}`,
      number: '',
      cvv: '',
      postalCode: '',
      serverError: null,
      submitting: false,
      activePlanType: props.initialPlanType,
      verifiedCoupon: this.props.coupon,
      couponCode: this.props.coupon ? this.props.coupon.code : null,
      isCouponApplied: false,
      couponError: '',
      isCouponInputExpanded: false,
      expiration: cc_expiration_month ? String(cc_expiration_month).padStart(2, '0') + ' / ' + (cc_expiration_year % 100) : ''
    };
  }

  static TEXT = {
    ...ProPageStrings.paymentText,
    submitText: StringConstantsMixin.strings.SUBMIT,
    headText: ProPageStrings.paymentText.creditCard
  };

  static CANCEL_URL = '/my/profile/edit';

  static ANNUAL_PLAN = 'annual';

  static THREE_YEAR_PLAN = 'threeYr';

  static PLANS = ['annual', 'threeYr'];

  static GIFT_CARD = 'GiftCard';

  componentDidMount() {
    if (this.state.verifiedCoupon && this.props.reactivation) {
      this.setState({ isCouponApplied: true, isCouponInputExpanded: true, isCouponInputShown: true, submitting: false });
    }
  }

  handleTypeClick = activePlanType => {
    this.setState({ activePlanType }, this.handleRemoveCoupon);
  };

  handleCreditCardFormChange = event => {
    const { name, value } = event.target;
    const payload = validatePaymentForm(name, value, this.state.errors, ProPageStrings.creditCardForm);
    this.setState({ ...payload });
  };

  handlePaymentEditSuccess = data => {
    window.location.href = LanguageSupportUtil.wrapUrlSafe(data.redirectUrl, this.props.locale);
  };

  handleRemoveCoupon = () => {
    this.setState({
      isCouponInputExpanded: false,
      couponCode: null,
      isCouponApplied: false,
      verifiedCoupon: null
    });
  };

  handleCouponCodeChange = event => {
    const { value } = event.target;
    this.setState({ couponCode: value, couponError: '' });
  };

  handleShowCouponCodeInput = () => {
    this.setState({ isCouponInputExpanded: true });
  };

  handleApplyCoupon = () => {
    this.handleCouponVerification(this.state.couponCode);
  };

  handlePaymentFormError = err => {
    let { errors, serverError } = this.state;

    let errorObj;
    try {
      errorObj = JSON.parse(err.responseText).errors;
    } catch (e) {
      errorObj = false;
    }

    if (errorObj) {
      if (errorObj.base) {
        serverError = typeof errorObj.base[0] === 'string' ? errorObj.base[0] : UpdatePaymentForm.TEXT.serverError;
      }
      if (errorObj.cc_number) {
        errors.number = errorObj.cc_number[0];
      }

      if (errorObj.cc_expiration_month) {
        errors.expiration = errorObj.cc_expiration_month[0];
      }

      if (errorObj.cc_expiration_year) {
        errors.expiration = errorObj.cc_expiration_year[0];
      }

      if (errorObj.cc_csc) {
        errors.cvv = errorObj.cc_csc[0];
      }
    } else {
      serverError = UpdatePaymentForm.TEXT.serverError;
    }
    this.setState({ errors, serverError, submitting: false });
  };

  getHeaderAndFooterText() {
    let text = UpdatePaymentForm.TEXT;
    let footText = '';

    if (this.props.reactivation) {
      text = { ...text, headText: <FormattedMessage defaultMessage="Pick your plan" /> };

      if (this.state.activePlanType === UpdatePaymentForm.ANNUAL_PLAN) {
        footText = (
          <PaymentFinePrint
            activePlanType={this.state.activePlanType}
            currencyCode={this.props.currencyCode}
            isCouponApplied={this.props.isCouponApplied}
            planTypes={this.props.planTypes}
            verifiedCoupon={this.state.verifiedCoupon}
          />
        );
      } else {
        footText = UpdatePaymentForm.TEXT.threeYearFootText;
      }
    }

    return { text, footText };
  }

  getSubmissionData() {
    const [cc_expiration_month, cc_expiration_year] = this.state.expiration.split(' / ');

    let data = {
      user: {
        cc_number: this.state.number,
        cc_expiration_month,
        cc_expiration_year,
        cc_csc: this.state.cvv,
        zip: this.state.postalCode,
        authenticity_token: this.props.authenticityToken
      }
    };

    // requires additional params if currently in the reactivation flow
    if (this.props.reactivation) {
      data = {
        ...data,
        plan_type: this.state.activePlanType,
        user: {
          ...data.user,
          user_entered_coupon_code: this.state.verifiedCoupon ? this.state.verifiedCoupon.code : null,
          reactivating: true
        }
      };
    }

    return data;
  }

  noGiftCard() {
    if (!this.props.reactivation || !this.state.verifiedCoupon) {
      return true;
    }

    return this.state.verifiedCoupon.type !== UpdatePaymentForm.GIFT_CARD;
  }

  failedFormValidation() {
    let validation;
    let validationErrors = { ...this.state.errors };

    for (const name in this.state.errors) {
      validation = validatePaymentForm(name, this.state[name], this.state.errors, ProPageStrings.creditCardForm);
      validationErrors = { ...validationErrors, ...validation.errors };
    }

    this.setState({ errors: validationErrors });

    return hasErrors(validationErrors);
  }

  submitCreditCardForm = e => {
    e.preventDefault();
    if (this.state.submitting) {
      return;
    }

    this.setState({ submitting: true });

    if (this.noGiftCard()) {
      if (this.failedFormValidation()) {
        this.setState({ submitting: false });
        return;
      }
    }

    this.setState({ errors: {} });

    const submissionData = this.getSubmissionData();

    ServerCommunicationUtil.putApiEndpoint(
      this.props.updateBillingUrl,
      submissionData,
      this.handlePaymentEditSuccess,
      this.handlePaymentFormError,
      () => {},
      { 'X-Language-Locale': this.props.locale }
    );
  };

  handleCouponVerification = couponCode => {
    if (this.state.submitting) {
      return;
    }

    this.setState({ submitting: true });
    const payload = {
      coupon_code: couponCode
    };
    ServerCommunicationUtil.postApiEndpoint(
      '/api/alltrails/coupons/verify',
      payload,
      data => {
        this.setState({ verifiedCoupon: data[0], isCouponApplied: true, submitting: false });
      },
      err => {
        this.setState({
          isCouponApplied: false,
          couponError: ProPageStrings.creditCardForm.couponCode.validation.generic,
          submitting: false
        });
      },
      () => {}
    );
  };

  renderServerError() {
    return <div className={this.state.serverError ? styles.serverError : ''}>{this.state.serverError}</div>;
  }

  renderPlanOptions() {
    if (!this.props.reactivation) {
      return;
    }

    return (
      <>
        <PlanTypeCardCollection
          activePlanType={this.state.activePlanType}
          currencyCode={this.props.currencyCode}
          handler={this.handleTypeClick}
          isCouponApplied={this.state.isCouponApplied}
          isSuccess={this.state.isCouponApplied}
          planTypes={this.props.planTypes}
          verifiedCoupon={this.state.verifiedCoupon}
        />
        <div className={styles.divider} />
      </>
    );
  }

  renderCreditCardForm() {
    return (
      <>
        {this.props.reactivation && <div className={styles.billingText}>{UpdatePaymentForm.TEXT.enterBillingInformation}</div>}
        <form id="update-payment-form" onSubmit={this.submitCreditCardForm}>
          <CreditCardForm
            expiration={this.state.expiration}
            changeHandler={this.handleCreditCardFormChange}
            cvv={this.state.cvv}
            errors={this.state.errors}
            name={this.state.name}
            number={this.state.number}
            postalCode={this.state.postalCode}
            submitHandler={this.submitCreditCardForm}
            text={UpdatePaymentForm.TEXT.creditCardForm}
            setError={(name, error) => this.setState({ errors: { ...this.state.errors, [name]: error } })}
            toggleCouponCode={() => this.setState({ isCouponInputExpanded: !this.state.isCouponInputExpanded })}
            isCouponInputExpanded={this.state.isCouponInputExpanded}
            isCouponInputShown={this.state.isCouponInputShown}
          />
          {this.props.reactivation && (
            <CouponCode
              applyHandler={this.handleApplyCoupon}
              changeHandler={this.handleCouponCodeChange}
              errorMessage={this.state.couponError}
              expanded={this.state.isCouponInputExpanded}
              isSuccess={this.state.isCouponApplied}
              removeHandler={this.handleRemoveCoupon}
              value={this.state.couponCode}
            />
          )}
          {this.renderServerError()}
          <div>
            <FormButton disabled={this.state.submitting} value={UpdatePaymentForm.TEXT.submitText} />
            {!this.props.reactivation && (
              <a className={buttonStyles.text} href={UpdatePaymentForm.CANCEL_URL}>
                <FormattedMessage defaultMessage="Cancel" />
              </a>
            )}
          </div>
        </form>
      </>
    );
  }

  render() {
    const { text, footText } = this.getHeaderAndFooterText();

    return (
      <CustomIntlProvider>
        <Wrapper extraContainerClass={styles.wrapperContainerClasses} foot={footText} text={text}>
          {this.renderPlanOptions()}
          <div className={baseStyles.section}>
            <div>{this.renderCreditCardForm()}</div>
          </div>
        </Wrapper>
      </CustomIntlProvider>
    );
  }
}

UpdatePaymentForm.defaultProps = {};

UpdatePaymentForm.propTypes = {
  locale: PropTypes.string,
  recurlyPublicKey: PropTypes.string,
  updateBillingUrl: PropTypes.string,
  initialPlanType: PropTypes.string,
  formAuthenticityToken: PropTypes.string,
  planTypes: PropTypes.arrayOf(
    PropTypes.shape({
      planCode: PropTypes.string,
      priceMonth: PropTypes.string,
      priceTotal: PropTypes.string,
      numberOfMonths: PropTypes.string,
      showDiscount: PropTypes.bool,
      allowCoupon: PropTypes.bool
    })
  ),
  formData: PropTypes.shape({
    billing_first_name: PropTypes.string,
    billing_last_name: PropTypes.string,
    cc_expiration_month: PropTypes.number,
    cc_expiration_year: PropTypes.number
  })
};

export default UpdatePaymentForm;
